import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { encodeFunctionData, type Address, type Hex } from 'viem';
import { z } from 'zod';
import { deriveExternalFixedRouteId, encodeExternalFixedRouteIdentity, type ExternalFixedRouteIdentityInput, type ExternalFixedRoute,
  encodeSelectedExternalFixedRouteIdentity, deriveSelectedExternalFixedRouteId } from './externalFixedRouteIdentity.js';
import { experimentalExternalFixedRouteOrderAbi as abi } from './externalFixedRouteOrderAbi.js';
import { encodeExternalOrderTriggerPrice } from './externalOrderTriggerPrice.js';

const positive128 = z.bigint().min(1n).max((1n << 128n) - 1n);
const positive256 = z.bigint().min(1n).max((1n << 256n) - 1n);
const context = z.strictObject({ globalEpoch: positive256, routeRevision: positive256 });
const trigger = z.strictObject({ price: z.string(), atOrBelow: z.boolean(), latchAfterFirstFill: z.boolean() });
const leg = z.strictObject({ minimumTotalNetOutput: positive128,
  expiry: z.bigint().min(0n).max((1n << 48n) - 1n), trigger });
const single = z.strictObject({ expected: context, buy: z.boolean(), maximumInput: positive128,
  minimumTotalNetOutput: positive128, expiry: leg.shape.expiry, trigger: trigger.nullable() });
const linked = z.strictObject({ expected: context, maximumInput: positive128, first: leg, second: leg });

function registrationCodec<const ChainId extends 4663 | 46630>(
  encodeExternalFixedRouteIdentity: (input: ExternalFixedRouteIdentityInput<ChainId>) =>
    { canonicalRoute: ExternalFixedRoute; identityHash: Hex; engine: Address },
  deriveExternalFixedRouteId: (input: ExternalFixedRouteIdentityInput<ChainId>, feeBps?: number) => Hex) {
  function routeContext(identity: ExternalFixedRouteIdentityInput<ChainId>, feeBps?: number) {
    const encoded = encodeExternalFixedRouteIdentity(identity);
    const base = encoded.canonicalRoute.tokens[0]!;
    const quote = encoded.canonicalRoute.tokens.at(-1)!;
    const routeId = deriveExternalFixedRouteId(identity, feeBps);
    function encodeTrigger(value: z.infer<typeof trigger> | null) {
      if (value === null) return { numerator: 0n, denominator: 0n, atOrBelow: false, latchAfterFirstFill: false };
      return { ...encodeExternalOrderTriggerPrice({ price: value.price, baseDecimals: base.decimals, quoteDecimals: quote.decimals }),
        atOrBelow: value.atOrBelow, latchAfterFirstFill: value.latchAfterFirstFill };
    }
    return { encoded, routeId, encodeTrigger };
  }

  /** Unsigned encoding only. Caller must verify this route/revision/epoch against
   * the concrete engine before owner authorization. No observed identity, live
   * expiry, token admission, simulation, signing or submission is asserted here.
   * A null trigger explicitly means a limit order governed by its net-output floor.
   */
  function prepareExternalOrderRegistration(identity: ExternalFixedRouteIdentityInput<ChainId>, input: z.infer<typeof single>, feeBps?: number) {
    const terms = single.parse(input);
    const route = routeContext(identity, feeBps);
    const data = encodeFunctionData({ abi, functionName: 'register', args: [route.routeId, terms.expected,
      terms.buy, terms.maximumInput, terms.minimumTotalNetOutput, terms.expiry, route.encodeTrigger(terms.trigger)] });
    return { terms, routeId: route.routeId, routeIdentityHash: route.encoded.identityHash,
      route: route.encoded.canonicalRoute, call: { target: route.encoded.engine, value: 0n, data },
      authorizationRequired: 'vault-owner' as const, submissionAuthorized: false as const };
  }

  /** Both SELL legs share one quantity; neither becomes OCO. Leg order is preserved. */
  function prepareExternalPairedRegistration(identity: ExternalFixedRouteIdentityInput<ChainId>, input: z.infer<typeof linked>, feeBps?: number) {
    const terms = linked.parse(input);
    if (terms.first.trigger.atOrBelow === terms.second.trigger.atOrBelow) throw new Error('Paired triggers must oppose each other');
    const route = routeContext(identity, feeBps);
    const first = { ...terms.first, trigger: route.encodeTrigger(terms.first.trigger) };
    const second = { ...terms.second, trigger: route.encodeTrigger(terms.second.trigger) };
    const data = encodeFunctionData({ abi, functionName: 'registerAllocation',
      args: [route.routeId, terms.expected, terms.maximumInput, first, second] });
    return { terms, routeId: route.routeId, routeIdentityHash: route.encoded.identityHash,
      route: route.encoded.canonicalRoute, call: { target: route.encoded.engine, value: 0n, data },
      allocationPolicy: 'shared-remaining-quantity' as const,
      authorizationRequired: 'vault-owner' as const, submissionAuthorized: false as const };
  }
  return { prepareExternalOrderRegistration, prepareExternalPairedRegistration };
}

export const { prepareExternalOrderRegistration, prepareExternalPairedRegistration } =
  registrationCodec<4663>(encodeExternalFixedRouteIdentity, deriveExternalFixedRouteId);
const selectedRegistration = registrationCodec<typeof SELECTED_ACCOUNT_CHAIN_ID>(
  encodeSelectedExternalFixedRouteIdentity, deriveSelectedExternalFixedRouteId);
export const prepareSelectedExternalOrderRegistration = selectedRegistration.prepareExternalOrderRegistration;
export const prepareSelectedExternalPairedRegistration = selectedRegistration.prepareExternalPairedRegistration;
